/* ==========================================================================
   WebGas UX — Maruse
   Comportamenti dei fix PDP: barra d'acquisto sticky e opzione regalo.
   Non duplica la logica di carrello del tema: la barra sticky inoltra il
   click al pulsante "Add to cart" originale, cosi' resta valida tutta la
   gestione AJAX, varianti e app di Beyond.
   ========================================================================== */

class WgStickyAtc extends HTMLElement {
  connectedCallback() {
    // La sezione WebGas espone data-wg-pdp-root, quella nativa di Beyond
    // espone data-product-id: la barra funziona con entrambe.
    this.root =
      document.querySelector(`[data-wg-pdp-root="${this.dataset.id}"]`) ||
      document.querySelector(`[data-product-id="${this.dataset.id}"]`);
    if (!this.root) return;

    this.sourceButton = this.root.querySelector('.product-buy-buttons--primary');
    this.sourcePrice = this.root.querySelector('.product-price--original');
    this.sourceCompare = this.root.querySelector('.product-price--compare');

    this.button = this.querySelector('.wg-sticky-atc--button');
    this.price = this.querySelector('.wg-sticky-atc--price-value');
    this.compare = this.querySelector('.wg-sticky-atc--compare');

    if (!this.sourceButton || !this.button) return;

    this.bindClick();
    this.observeVariant();
    this.setupVisibility();
    this.syncButtonState();
  }

  disconnectedCallback() {
    if (this.priceObserver) this.priceObserver.disconnect();
    if (this.buttonObserver) this.buttonObserver.disconnect();
    if (this.ctaObserver) this.ctaObserver.disconnect();
    if (this.footerObserver) this.footerObserver.disconnect();
  }

  /* Il click sulla barra attiva il pulsante reale del form prodotto. */
  bindClick() {
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.sourceButton.disabled) return;

      // Se l'app di personalizzazione o le opzioni richiedono un input,
      // portiamo l'utente al form invece di forzare l'aggiunta al carrello.
      if (this.dataset.scrollToForm === 'true') {
        this.scrollToForm(this.sourceButton);
        return;
      }

      // Guardia automatica: se nel form c'e' un campo obbligatorio ancora
      // vuoto (tipicamente la personalizzazione Zepto), aggiungere al
      // carrello dalla barra produrrebbe un errore o un ordine incompleto.
      const pending = this.findPendingField();
      if (pending) {
        this.scrollToForm(pending);
        if (typeof pending.focus === 'function') pending.focus({ preventScroll: true });
        return;
      }

      this.sourceButton.click();
    });
  }

  /* Primo campo obbligatorio non compilato dentro il form prodotto. */
  findPendingField() {
    const form = this.sourceButton.closest('form');
    if (!form) return null;

    const fields = form.querySelectorAll('input[required], select[required], textarea[required]');

    for (const field of fields) {
      if (field.disabled || field.type === 'hidden' || field.offsetParent === null) continue;
      if (field.type === 'checkbox' || field.type === 'radio') {
        const group = form.querySelectorAll(`[name="${field.name}"]`);
        const checked = Array.from(group).some((item) => item.checked);
        if (!checked) return field;
        continue;
      }
      if (!field.value || field.value.trim() === '') return field;
    }

    return null;
  }

  scrollToForm(target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /* Tiene allineati prezzo e prezzo barrato al cambio variante. */
  observeVariant() {
    if (!this.sourcePrice || !this.price) return;

    const sync = () => {
      this.price.innerHTML = this.sourcePrice.innerHTML;

      if (this.compare && this.sourceCompare) {
        const visible = this.sourceCompare.style.display !== 'none' && this.sourceCompare.innerHTML.trim() !== '';
        this.compare.innerHTML = visible ? this.sourceCompare.innerHTML : '';
        this.compare.style.display = visible ? 'inline' : 'none';
      }
    };

    sync();

    this.priceObserver = new MutationObserver(sync);
    this.priceObserver.observe(this.sourcePrice, { childList: true, characterData: true, subtree: true });

    if (this.sourceCompare) {
      this.priceObserver.observe(this.sourceCompare, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
    }
  }

  /* Replica lo stato del pulsante originale, incluso l'esaurito. */
  syncButtonState() {
    const sync = () => {
      const disabled = this.sourceButton.hasAttribute('disabled');
      this.button.disabled = disabled;

      const label = this.sourceButton.querySelector('.product-buy-buttons--cta-text');
      const target = this.button.querySelector('.wg-sticky-atc--button-text');
      if (label && target) target.textContent = label.textContent.trim();
    };

    sync();

    this.buttonObserver = new MutationObserver(sync);
    this.buttonObserver.observe(this.sourceButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-busy'],
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /* Modalita' "always": visibile per tutta la pagina.
     Modalita' "after-cta": compare quando il pulsante originale esce dal viewport.
     In entrambi i casi la barra si ritira sul footer, altrimenti da desktop
     resterebbe sopra i link e la newsletter a fine pagina. */
  setupVisibility() {
    const mode = this.dataset.mode || 'after-cta';

    this.ctaOut = mode === 'always';
    this.footerIn = false;

    if (!('IntersectionObserver' in window)) {
      this.setAttribute('data-wg-visible', 'true');
      return;
    }

    if (mode !== 'always') {
      this.ctaObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.ctaOut = !entry.isIntersecting;
          });
          this.updateVisibility();
        },
        { rootMargin: '0px 0px -40px 0px', threshold: 0 }
      );

      this.ctaObserver.observe(this.sourceButton);
    }

    const footer = document.querySelector('.footer--root') || document.querySelector('footer');

    if (footer) {
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.footerIn = entry.isIntersecting;
          });
          this.updateVisibility();
        },
        { threshold: 0 }
      );

      this.footerObserver.observe(footer);
    }

    this.updateVisibility();
  }

  updateVisibility() {
    const visible = this.ctaOut && !this.footerIn;
    this.setAttribute('data-wg-visible', visible ? 'true' : 'false');
  }
}

if (!customElements.get('wg-sticky-atc')) {
  customElements.define('wg-sticky-atc', WgStickyAtc);
}

class WgGiftOption extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.wg-gift--checkbox');
    this.panel = this.querySelector('.wg-gift--panel');
    this.message = this.querySelector('.wg-gift--message');
    this.counter = this.querySelector('.wg-gift--counter-value');

    if (!this.checkbox) return;

    this.checkbox.addEventListener('change', () => this.toggle());
    if (this.message) this.message.addEventListener('input', () => this.updateCounter());

    this.toggle();
  }

  /* Quando l'opzione non e' attiva i campi restano disabilitati, cosi' non
     vengono inviati come proprieta' della riga d'ordine. */
  toggle() {
    const active = this.checkbox.checked;

    if (this.panel) this.panel.hidden = !active;
    if (this.message) {
      this.message.disabled = !active;
      if (!active) this.message.value = '';
    }

    this.updateCounter();
  }

  updateCounter() {
    if (!this.counter || !this.message) return;
    this.counter.textContent = String(this.message.value.length);
  }
}

if (!customElements.get('wg-gift-option')) {
  customElements.define('wg-gift-option', WgGiftOption);
}

class WgLowStock extends HTMLElement {
  connectedCallback() {
    this.textNode = this.querySelector('.wg-low-stock--text');
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.textOne = this.dataset.textOne || '';
    this.textFew = this.dataset.textFew || '';

    const json = this.querySelector('.wg-low-stock--json');
    try {
      this.variants = json ? JSON.parse(json.textContent) : [];
    } catch (error) {
      this.variants = [];
    }

    if (!this.textNode || this.threshold < 1) return;

    this.listen();
  }

  /* Il tema emette l'evento a ogni cambio variante: agganciamo quello invece
     di riascoltare i select, cosi' restiamo allineati anche con le app. */
  listen() {
    const handler = ({ detail }) => {
      const variant = detail && detail.variant;
      if (!variant) return this.render(null);

      const match = this.variants.find((item) => item.id === variant.id);
      this.render(match || null);
    };

    const event = `theme:product:${this.dataset.id}:variantChanged`;

    if (typeof window.on === 'function') {
      window.on(event, handler);
    } else {
      window.addEventListener(event, handler);
    }
  }

  /* Mostra il badge solo quando la scorta e' tracciata e sotto soglia.
     La quantita' precisa non viene mai scritta a schermo. */
  render(variant) {
    if (!variant || !variant.available || !variant.tracked) return this.hide();

    const quantity = variant.quantity;
    if (quantity < 1 || quantity > this.threshold) return this.hide();

    this.textNode.textContent = quantity === 1 ? this.textOne : this.textFew;
    this.hidden = false;
  }

  hide() {
    this.hidden = true;
  }
}

if (!customElements.get('wg-low-stock')) {
  customElements.define('wg-low-stock', WgLowStock);
}
